/**
 * LevNutri — Configurações do Consultório (CRN, valores, metas, perfil de
 * atendimento, tabela nutricional, horários, PIX e WhatsApp de notificação).
 * Extraído do Dashboard: agora abre pelo card "Consultório" em Configurações.
 */

import React, { useState, useEffect } from 'react';
import { settingsService, authService } from '../services/api';
import { useToast } from '../contexts/ToastContext';
import { Ico, emo } from './ui/Ico';

const C = {
  primary:  '#d4a574',
  dark:     '#5a3e2b',
  darker:   '#2f2a26',
  bg:       '#fbf8f5',
  border:   '#f6f0ea',
};


// ── Settings field ─────────────────────────────────────────────────────────────
function SettField({ label, value, onChange, type = 'text', step }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
      <label style={{ fontSize: '0.73rem', fontWeight: 600, color: C.dark, textTransform: 'uppercase', letterSpacing: '.04em' }}>
        {emo(label)}
      </label>
      <input
        type={type} value={value ?? ''} step={step}
        onChange={e => onChange(type === 'number' ? (parseFloat(e.target.value) || 0) : e.target.value)}
        style={{
          padding: '0.45rem 0.65rem', border: `1px solid ${C.border}`, borderRadius: '8px',
          fontSize: '0.88rem', color: C.darker, background: C.bg, outline: 'none', width: '100%', boxSizing: 'border-box',
        }}
      />
    </div>
  );
}

// ── Settings Modal ─────────────────────────────────────────────────────────────
export default function ConsultorioModal({ onClose, onSaved, inline = false }) {
  const { showSuccess, showError } = useToast();
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    settingsService.get()
      .then(sRes => { if (sRes.success) setS(sRes.data); })
      .catch(() => showError('Erro ao carregar configurações'));
  }, []); // eslint-disable-line

  const set = (k, v) => setS(prev => ({ ...prev, [k]: v }));

  const handleSave = async () => {
    setSaving(true);
    try {
      const sRes = await settingsService.save(s);
      if (sRes.success) {
        // Atualiza CRN no localStorage para refletir imediatamente no PrintLayout
        const stored = authService.getCurrentUser();
        if (stored) localStorage.setItem('user', JSON.stringify({ ...stored, crn: s.crn ?? '' }));
        showSuccess('Configurações salvas!'); onSaved(); onClose();
      }
      else showError('Erro ao salvar configurações');
    } catch { showError('Erro de conexão ao salvar configurações'); }
    finally { setSaving(false); }
  };

  const overlay = {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,.5)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    zIndex: 9998, padding: '1rem',
  };
  const box = {
    background: '#ffffff', borderRadius: '16px', padding: '1.75rem',
    width: '100%', maxWidth: '520px', maxHeight: '90vh',
    overflowY: 'auto', boxShadow: '0 24px 64px rgba(0,0,0,.2)',
  };
  const fs = { border: `1px solid ${C.border}`, borderRadius: '10px', padding: '1rem', margin: 0 };
  const fsLeg = { padding: '0 0.4rem', fontSize: '0.7rem', fontWeight: 600, color: C.dark, textTransform: 'uppercase', letterSpacing: '.06em' };
  // Convenção 1-7 (date('N') do backend: Seg=1 … Dom=7) — igual à aba Bot
  const DIAS = ['Seg','Ter','Qua','Qui','Sex','Sáb','Dom'];

  return (
    <div style={inline ? undefined : overlay} onClick={inline ? undefined : onClose}>
      <div style={inline ? undefined : box} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
          <h2 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 700, color: C.darker }}><Ico e="⚙" sp />Configurações do Consultório</h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '1.2rem', cursor: 'pointer', color: '#a39b93', padding: 0 }}><Ico e="✕" /></button>
        </div>

        {!s ? (
          <p style={{ textAlign: 'center', color: '#a39b93', padding: '2rem 0' }}>Carregando...</p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            <fieldset style={fs}>
              <legend style={fsLeg}>Informações da Nutricionista</legend>
              <div style={{ marginTop: '0.5rem' }}>
                <SettField
                  label="CRN"
                  value={s.crn ?? ''}
                  onChange={v => set('crn', v)}
                  placeholder="ex: 12345"
                />
                <p style={{ margin: '0.25rem 0 0', fontSize: '0.72rem', color: '#736b64' }}>
                  Aparece em todos os documentos gerados (planos, relatórios, prescrições)
                </p>
              </div>
            </fieldset>

            <fieldset style={fs}>
              <legend style={fsLeg}>Valores de Consulta</legend>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', marginTop: '0.5rem' }}>
                <SettField label="Presencial (R$)" type="number" step="0.01" value={s.valor_consulta_presencial} onChange={v => set('valor_consulta_presencial', v)} />
                <SettField label="Online (R$)" type="number" step="0.01" value={s.valor_consulta_online} onChange={v => set('valor_consulta_online', v)} />
                <SettField label="Aluguel do Espaço (R$/mês)" type="number" step="0.01" value={s.valor_aluguel_espaco} onChange={v => set('valor_aluguel_espaco', v)} />
              </div>
            </fieldset>

            <fieldset style={fs}>
              <legend style={fsLeg}>Metas Mensais</legend>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', marginTop: '0.5rem' }}>
                <SettField label="Receita Meta (R$)" type="number" step="1" value={s.meta_receita_mes} onChange={v => set('meta_receita_mes', v)} />
                <SettField label="Nº de Consultas" type="number" step="1" value={s.meta_consultas_mes} onChange={v => set('meta_consultas_mes', v)} />
              </div>
            </fieldset>

            <fieldset style={{ border: `1px solid ${C.border}`, borderRadius: '8px', padding: '12px 16px', margin: 0, width: '100%', boxSizing: 'border-box' }}>
              <legend style={{ fontSize: '11px', fontWeight: 600, color: C.primary, textTransform: 'uppercase', letterSpacing: '.06em', padding: '0 4px' }}>
                <Ico e="👥" sp />Perfil de Atendimento
              </legend>
              <p style={{ fontSize: '12px', color: '#736b64', margin: '0 0 12px' }}>
                Define os tipos de serviço disponíveis na geração de planos com IA.
              </p>
              {emo([
                { value: 'female', label: '♀ Feminino', desc: 'Serviços especializados para mulheres (hormônios, emagrecimento feminino, etc.)' },
                { value: 'male',   label: '♂ Masculino', desc: 'Serviços para homens (hipertrofia, performance, saúde cardiovascular, etc.)' },
                { value: 'mixed',  label: '⚥ Misto',     desc: 'Exibe todos os serviços — femininos e masculinos' },
              ].map((opt, i, arr) => (
                <label key={opt.value} style={{
                  display: 'flex', alignItems: 'center', gap: '10px',
                  padding: '9px 4px', cursor: 'pointer',
                  borderBottom: i < arr.length - 1 ? `1px solid #f6f0ea` : 'none',
                }}>
                  <input
                    type="radio"
                    name="gender_focus"
                    value={opt.value}
                    checked={(s.gender_focus ?? 'female') === opt.value}
                    onChange={e => set('gender_focus', e.target.value)}
                    style={{ accentColor: C.primary, width: '16px', height: '16px', flexShrink: 0 }}
                  />
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: '13px', fontWeight: 600, color: C.darker, wordBreak: 'break-word' }}>
                      {emo(opt.label)}
                      {(s.gender_focus ?? 'female') === opt.value && <span style={{ marginLeft: '6px', fontSize: '11px', color: C.primary, fontWeight: 400 }}><Ico e="✅" sp />ativo</span>}
                    </div>
                    <div style={{ fontSize: '11px', color: '#736b64', marginTop: '1px' }}>{emo(opt.desc)}</div>
                  </div>
                </label>
              )))}
            </fieldset>

            {/* Tabela Nutricional de Referência foi movida para a tela
                "Substituições de Alimentos" (menu próprio). */}

            <fieldset style={fs}>
              <legend style={fsLeg}>Horários de Atendimento</legend>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', marginTop: '0.5rem', marginBottom: '0.75rem' }}>
                <SettField label="Início" type="time" value={s.horario_inicio} onChange={v => set('horario_inicio', v)} />
                <SettField label="Fim" type="time" value={s.horario_fim} onChange={v => set('horario_fim', v)} />
                <div style={{ gridColumn: '1 / -1' }}>
                  <p style={{ margin: '0 0 0.3rem', fontSize: '0.73rem', fontWeight: 600, color: C.dark, textTransform: 'uppercase' }}>
                    Duração da Consulta
                  </p>
                  <select
                    value={s.slot_duration ?? 60}
                    onChange={e => set('slot_duration', Number(e.target.value))}
                    style={{
                      width: '100%', padding: '0.45rem 0.75rem',
                      border: `1px solid ${C.border}`, borderRadius: '8px',
                      fontSize: '0.875rem', color: C.darker, background: '#ffffff',
                      cursor: 'pointer',
                    }}
                  >
                    <option value={30}>30 minutos</option>
                    <option value={45}>45 minutos</option>
                    <option value={60}>60 minutos (padrão)</option>
                    <option value={90}>90 minutos</option>
                  </select>
                  <p style={{ margin: '0.3rem 0 0', fontSize: '0.72rem', color: '#736b64' }}>
                    Define o intervalo entre slots usados pelo bot de agendamento
                  </p>
                </div>
                {/* Chave PIX foi movida para a tela do Bot (WhatsApp) — evita duplicação.
                    A fonte única passa a ser bot_config.pix_key. */}
                <div style={{ gridColumn: '1 / -1' }}>
                  <p style={{ margin: '0 0 0.3rem', fontSize: '0.73rem', fontWeight: 600, color: C.dark, textTransform: 'uppercase' }}>
                    <Ico e="📱" sp />WhatsApp — Notificações da Nutricionista
                  </p>
                  <input
                    type="text"
                    inputMode="numeric"
                    value={s.whatsapp_notificacao ?? s.whatsapp_erika ?? ''}
                    onChange={e => set('whatsapp_notificacao', e.target.value)}
                    placeholder="Ex: 5521999998888"
                    style={{
                      width: '100%', padding: '0.45rem 0.75rem', boxSizing: 'border-box',
                      border: `1px solid ${C.border}`, borderRadius: '8px',
                      fontSize: '0.875rem', color: C.darker, background: '#ffffff',
                    }}
                  />
                  <p style={{ margin: '0.3rem 0 0', fontSize: '0.72rem', color: '#736b64' }}>
                    Seu celular pessoal, com DDI e DDD (sem +). Recebe avisos de fichas preenchidas,
                    comprovantes PIX, pedidos de remarcação e o resumo semanal de pacientes inativas.
                    {s.tem_canal_bot
                      ? ' É o mesmo número de Bot WhatsApp → Meu número: alterar aqui altera lá.'
                      : ' Os avisos saem pelo Bot WhatsApp; conecte seu número em Bot WhatsApp para recebê-los.'}
                  </p>
                </div>
              </div>
              <p style={{ margin: '0 0 0.4rem', fontSize: '0.73rem', fontWeight: 600, color: C.dark, textTransform: 'uppercase' }}>
                Dias de Atendimento
              </p>
              <div style={{ display: 'flex', gap: '0.35rem', flexWrap: 'wrap' }}>
                {DIAS.map((d, i) => {
                  const num = i + 1; // Seg=1 … Dom=7
                  const dias = s.dias_atendimento || [1,2,3,4,5];
                  const active = dias.includes(num);
                  return (
                    <button key={num} type="button"
                      onClick={() => {
                        const nd = active ? dias.filter(x => x !== num) : [...dias, num].sort((a,b)=>a-b);
                        set('dias_atendimento', nd);
                      }}
                      style={{
                        padding: '0.28rem 0.6rem', borderRadius: '6px', fontSize: '0.78rem', cursor: 'pointer',
                        border: `1px solid ${active ? C.primary : C.border}`,
                        background: active ? C.primary : '#ffffff',
                        color: active ? '#ffffff' : C.dark,
                        fontWeight: active ? 700 : 400,
                        transition: 'all .15s',
                      }}
                    >
                      {d}
                    </button>
                  );
                })}
              </div>
            </fieldset>
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.6rem', marginTop: '1.5rem' }}>
          <button onClick={onClose} style={{
            padding: '0.6rem 1.25rem', background: '#ffffff', color: C.dark,
            border: `1px solid ${C.border}`, borderRadius: '8px', cursor: 'pointer', fontWeight: 600,
          }}>Cancelar</button>
          <button onClick={handleSave} disabled={saving || !s} style={{
            padding: '0.6rem 1.5rem', background: saving ? '#ebe3da' : C.primary,
            color: '#ffffff', border: 'none', borderRadius: '8px',
            cursor: saving ? 'not-allowed' : 'pointer', fontWeight: 600,
          }}>
            {saving ? 'Salvando...' : 'Salvar Configurações'}
          </button>
        </div>
      </div>
    </div>
  );
}
